import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Link, Switch } from '@material-ui/core';
import AutorenewOutlinedIcon from '@material-ui/icons/AutorenewOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Field } from '../../../helpers/renderCountDebug';
import { mainTheme } from '../../../Theme';
import { LightTooltip } from './LightTooltip';

// Explicit request: Auto Follow Up used to be a checkbox floating at the right of the outcome row. It is now a bar of the same height as the outcome
// tabs, in the same place, with a switch. The email template still has its own field below (it is not part of the bar). The saved value is the same:
// isAutoFollowUp (on / off).
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        bar: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.25),
            boxSizing: 'border-box',
            flex: '1 1 0',
            minWidth: 0,
            maxWidth: 440,
            marginLeft: 'auto',
            height: 36,
            padding: theme.spacing(0, 0.5, 0, 1.5),
            border: `1px solid ${mainTheme.CommonColor.LightGrey2}`,
            borderRadius: 8,
            backgroundColor: '#fff',
            transition: 'background-color 0.2s ease, border-color 0.2s ease',
        },
        barOn: {
            backgroundColor: 'var(--rv-accent-indigo-bg)',
            borderColor: 'transparent',
        },
        icon: {
            flexShrink: 0,
            color: theme.palette.text.secondary,
            transition: 'color 0.2s ease',
            '$barOn &': {
                color: 'var(--rv-accent-indigo)',
            },
        },
        label: {
            flexShrink: 0,
            fontSize: 13,
            fontWeight: 600,
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
            transition: 'color 0.2s ease',
            '$barOn &': {
                color: 'var(--rv-accent-indigo)',
            },
        },
        spacer: {
            flex: 1,
            minWidth: 0,
        },
        // Takes the room that is left and shortens with an ellipsis rather than running over the label next to it.
        warning: {
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: 12,
            color: 'var(--rv-accent-orange)',
            whiteSpace: 'nowrap',
        },
        addEmailLink: {
            flexShrink: 0,
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
        },
        infoIcon: {
            flexShrink: 0,
            fontSize: 18,
            color: theme.palette.text.secondary,
            cursor: 'help',
        },
        // The switch is a plain pill with a small white thumb inside it (MUI's own switch has a thumb larger than its track).
        switchRoot: {
            width: 36,
            height: 20,
            padding: 0,
            flexShrink: 0,
            overflow: 'visible',
        },
        switchBase: {
            padding: 2,
            color: '#fff',
            '&$switchChecked': {
                transform: 'translateX(16px)',
                color: '#fff',
                '& + $switchTrack': {
                    backgroundColor: theme.palette.primary.main,
                    opacity: 1,
                },
            },
            '&$switchChecked$switchDisabled + $switchTrack': {
                opacity: 0.45,
            },
        },
        switchThumb: {
            width: 16,
            height: 16,
            boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
        },
        switchTrack: {
            borderRadius: 10,
            backgroundColor: theme.palette.grey[400],
            opacity: 1,
            transition: 'background-color 0.2s ease',
        },
        switchChecked: {},
        switchDisabled: {},
    }),
);

interface AutoFollowUpBarProps {
    // Auto Follow Up emails the enquirer, so it is unavailable until the enquiry has an email address.
    hasEmail: boolean;
    onAddEmailClick: () => void;
}

export const AutoFollowUpBar: React.FC<AutoFollowUpBarProps> = ({ hasEmail, onAddEmailClick }) => {
    const classes = useStyles();

    return (
        <Field name="isAutoFollowUp" subscription={{ value: true }}>
            {({ input }) => {
                const isOn = !!input.value;
                return (
                    <div className={clsx(classes.bar, isOn && hasEmail && classes.barOn)} data-tour="enquiry-auto-follow-up">
                        <AutorenewOutlinedIcon className={classes.icon} fontSize="small" />
                        <span className={classes.label}>Auto follow-up</span>
                        {hasEmail ? (
                            <span className={classes.spacer} />
                        ) : (
                            <>
                                <span className={classes.warning}>Needs an email</span>
                                <Link component="button" type="button" className={classes.addEmailLink} onClick={onAddEmailClick}>
                                    Add email
                                </Link>
                            </>
                        )}
                        <LightTooltip
                            title="Turning this on will allow an email to be automatically sent to the enquirer at the due date/time, rather than a reminder being sent to the assignee."
                            placement="top"
                            arrow={true}
                        >
                            <InfoOutlinedIcon className={classes.infoIcon} />
                        </LightTooltip>
                        <Switch
                            color="default"
                            classes={{
                                root: classes.switchRoot,
                                switchBase: classes.switchBase,
                                thumb: classes.switchThumb,
                                track: classes.switchTrack,
                                checked: classes.switchChecked,
                                disabled: classes.switchDisabled,
                            }}
                            checked={isOn}
                            disabled={!hasEmail}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                input.onChange(event.target.checked);
                            }}
                            name="isAutoFollowUp"
                            inputProps={{ 'aria-label': 'Auto follow-up' }}
                        />
                    </div>
                );
            }}
        </Field>
    );
};
